/**
 * Sticky bottom bar shown while ≥1 invoice is picked, the same shape as the
 * customers list bar (features/customers/customers-selection-bar.tsx).
 *
 * The totals are computed in the browser: the customer card already holds every
 * invoice it shows, so unlike the customers list there is nothing to ask the
 * server for — and therefore no loading or stale-response states here.
 *
 * On the customer card this bar lives inside a Polaris `<Card>`, whose
 * `.Polaris-ShadowBevel` opens its own stacking context (`position: relative;
 * z-index: 0`). A `fixed` bar rendered inside that context is not really
 * fixed to the viewport for stacking purposes — later cards in the page paint
 * over it and swallow clicks on its buttons. Portaling straight to
 * `document.body` sidesteps the Card's stacking context entirely.
 */
import { forwardRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Currency from "components/format/currency/currency";
import { usePresenceRailWidth } from "features/presence/use-presence-rail-width";

type Props = {
  count: number;
  amountWithVat: number;
  amount: number;
  debt: number;
  showOnlySelected: boolean;
  onToggleShowOnlySelected: () => void;
  onClear: () => void;
};

const buttonClass =
  "inline-flex items-center gap-1.5 rounded-sm border border-[#E6E1DB] bg-white px-3 py-1.5 text-sm text-[#3F3227] transition-colors hover:bg-gray-50 whitespace-nowrap";

function TotalStat({
  testId,
  label,
  value,
}: {
  testId: string;
  label: string;
  value: number;
}) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-xs text-gray-500">{label}</span>
      <span
        data-testid={`invoice-selection-total-${testId}`}
        className="text-sm font-medium text-gray-900"
      >
        <Currency value={value} isSymbol isDecimal />
      </span>
    </div>
  );
}

const InvoicesSelectionBar = forwardRef<HTMLDivElement, Props>(
  function InvoicesSelectionBar(
    {
      count,
      amountWithVat,
      amount,
      debt,
      showOnlySelected,
      onToggleShowOnlySelected,
      onClear,
    },
    ref
  ) {
    const { t } = useTranslation();
    const { companyId } = useParams();
    // Keep the bar's right edge on the presence rail's left edge.
    const railWidth = usePresenceRailWidth(companyId);

    if (count === 0) return null;

    return createPortal(
      <div
        ref={ref}
        data-testid="invoices-selection-bar"
        className="fixed bottom-[var(--phone-bar-height,0px)] left-0 z-30 border-t border-[#E6E1DB] bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.08)] transition-[right] duration-200 ease-out"
        style={{ right: railWidth }}
      >
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
          <div className="text-sm font-medium text-gray-900">
            {t("selected_count", { count })}
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <TotalStat
              testId="with-vat"
              label={t("totalWithVat")}
              value={amountWithVat}
            />
            <TotalStat
              testId="without-vat"
              label={t("totalWithoutVat")}
              value={amount}
            />
            <TotalStat testId="debt" label={t("debt")} value={debt} />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              className={buttonClass}
              onClick={onToggleShowOnlySelected}
            >
              {showOnlySelected ? t("show_all_rows") : t("show_only_selected")}
            </button>
            <button type="button" className={buttonClass} onClick={onClear}>
              <X className="h-4 w-4" />
              {t("clear_selection")}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }
);

export default InvoicesSelectionBar;
